import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAdminPerformanceOverview } from '../services/adminDashboardService';
import '../styles/performanceMonitoringAdmin.css';

const RANGE_OPTIONS = [
  ['15m', '15 dk', '15 min'],
  ['1h', '1 saat', '1 hour'],
  ['6h', '6 saat', '6 hours'],
  ['24h', '24 saat', '24 hours'],
  ['7d', '7 gün', '7 days'],
  ['30d', '30 gün', '30 days'],
];

const number = (value, digits = 0) => Number(value || 0).toLocaleString(undefined, {
  maximumFractionDigits: digits,
});

const bytes = (value) => {
  const amount = Number(value || 0);
  if (amount < 1024) return `${number(amount)} B`;
  if (amount < 1024 ** 2) return `${number(amount / 1024, 1)} KB`;
  if (amount < 1024 ** 3) return `${number(amount / 1024 ** 2, 1)} MB`;
  return `${number(amount / 1024 ** 3, 2)} GB`;
};

const duration = (milliseconds) => {
  const value = Number(milliseconds || 0);
  return value >= 1000 ? `${number(value / 1000, 2)} sn` : `${number(value)} ms`;
};

function MetricCard({ label, value, detail, tone = '' }) {
  return <div className={`perfMetricCard ${tone}`}><span>{label}</span><strong>{value}</strong>{detail && <small>{detail}</small>}</div>;
}

function Section({ title, subtitle, children }) {
  return <section className="perfSection"><header><div><strong>{title}</strong>{subtitle && <small>{subtitle}</small>}</div></header>{children}</section>;
}

function Empty({ children }) {
  return <div className="perfEmpty">{children}</div>;
}

export default function PerformanceMonitoringAdmin({ tr = true, requireAdminUnlock = true }) {
  const [range, setRange] = useState('1h');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);
  const requestSequence = useRef(0);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const sequence = ++requestSequence.current;
    if (!quiet) setLoading(true);
    setError('');
    try {
      const next = await getAdminPerformanceOverview(range, {
        managementContext: !requireAdminUnlock,
      });
      if (mountedRef.current && sequence === requestSequence.current) setData(next);
    } catch (requestError) {
      if (mountedRef.current && sequence === requestSequence.current) {
        setError(requestError?.message || (tr ? 'Performans verileri alınamadı.' : 'Performance data could not be loaded.'));
      }
    } finally {
      if (mountedRef.current && sequence === requestSequence.current) setLoading(false);
    }
  }, [range, requireAdminUnlock, tr]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    }, 20_000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mountedRef.current = false;
      requestSequence.current += 1;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  const http = data?.http || {};
  const runtime = data?.runtime || {};
  const history = data?.history || null;
  const historicalRuntime = history?.runtime || {};
  const historicalDatabase = history?.database || {};
  const historicalSse = history?.sse || {};
  const historicalPresence = history?.presence || {};
  const historicalLocationQueue = history?.queues?.location || null;
  const database = data?.database || {};
  const pool = database.pool || {};
  const locationQueue = data?.queues?.location || {};
  const notificationQueue = data?.queues?.notifications || {};
  const auth = data?.auth || {};
  const slo = data?.slo || {};
  const recentRestartCount = history?.restarts ?? (data?.alerts?.recent || []).filter((alert) => alert.name === 'AileBackendRestarted').length;
  const connectionWaitP95 = historicalDatabase.connectionWaitP95Ms ?? database.connectionWait?.p95Ms;
  const locationPeriodTotals = historicalLocationQueue || locationQueue;
  const endpointSizeHeading = data?.source === 'prometheus' ? (tr ? 'p95 boyut' : 'p95 size') : (tr ? 'En büyük' : 'Largest');
  const statusText = slo.status === 'healthy'
    ? (tr ? 'Sağlıklı' : 'Healthy')
    : slo.status === 'critical'
      ? (tr ? 'Kritik' : 'Critical')
      : (tr ? 'Uyarı' : 'Warning');

  return <div className="performanceMonitoringAdmin">
    <div className="perfToolbar">
      <div>
        <strong>{tr ? 'Gerçek Zamanlı Performans' : 'Real-time Performance'}</strong>
        <small>{data?.generatedAt ? new Date(data.generatedAt).toLocaleString(tr ? 'tr-TR' : 'en-US') : (tr ? 'Ölçüm bekleniyor' : 'Waiting for metrics')}{data?.source === 'prometheus' ? ` · ${tr ? 'kalıcı Prometheus geçmişi' : 'persistent Prometheus history'}` : ''}</small>
      </div>
      <button type="button" onClick={() => load()} disabled={loading}>↻ {loading ? (tr ? 'Yükleniyor' : 'Loading') : (tr ? 'Yenile' : 'Refresh')}</button>
    </div>

    <div className="perfRanges" role="group" aria-label={tr ? 'Ölçüm aralığı' : 'Metrics range'}>
      {RANGE_OPTIONS.map(([key, trLabel, enLabel]) => <button key={key} type="button" className={range === key ? 'active' : ''} onClick={() => setRange(key)}>{tr ? trLabel : enLabel}</button>)}
    </div>

    {error && <div className="perfError">{error}</div>}
    {data?.partial && <div className="perfNotice">{tr ? 'Bu process seçilen süreden daha yeni başlatıldı; veriler process başlangıcından itibaren gösteriliyor.' : 'This process started more recently than the selected range; data is shown since process start.'}</div>}
    {data?.historicalUnavailable && <div className="perfNotice">{tr ? 'Prometheus geçmişi henüz erişilebilir değil; mevcut instance verisi gösteriliyor.' : 'Prometheus history is not available yet; current instance data is shown.'}</div>}
    {(data?.alerts?.active || []).length > 0 && <div className="perfActiveAlerts">
      <strong>{tr ? 'Aktif performans alarmları' : 'Active performance alerts'}</strong>
      {data.alerts.active.map((alert) => <div key={alert.fingerprint} className={alert.severity}><span>{alert.name}</span><small>{alert.summary}</small><time>{new Date(alert.startedAt).toLocaleString(tr ? 'tr-TR' : 'en-US')}</time></div>)}
    </div>}

    <div className="perfHealthRow">
      <div className={`perfHealthBadge ${slo.status || 'unknown'}`}><i /> <span>{tr ? 'Sistem durumu' : 'System status'}</span><strong>{statusText}</strong></div>
      <small>{tr ? 'İzleme sistemi kullanıcı içeriklerini veya kimliklerini kaydetmez.' : 'Monitoring does not store user content or identities.'}</small>
    </div>

    <div className="perfMetricGrid">
      <MetricCard label={tr ? 'İstek / dk' : 'Requests / min'} value={number(http.requestsPerMinute, 2)} detail={`${number(http.requests)} ${tr ? 'istek' : 'requests'}`} />
      <MetricCard label="p95" value={duration(http.p95Ms)} tone={http.p95Ms > 700 ? 'warning' : 'ok'} />
      <MetricCard label="p99" value={duration(http.p99Ms)} tone={http.p99Ms > 1500 ? 'danger' : 'ok'} />
      <MetricCard label="5xx" value={`%${number(http.errorRate, 3)}`} detail={`${number(http.errors5xx)} ${tr ? 'hata' : 'errors'}`} tone={http.errorRate > 0.1 ? 'danger' : 'ok'} />
      <MetricCard label={tr ? 'Anlık event-loop p95' : 'Current event-loop p95'} value={duration(runtime.eventLoopP95Ms)} tone={runtime.eventLoopP95Ms > 50 ? 'warning' : 'ok'} />
      <MetricCard label={tr ? 'Anlık CPU' : 'Current CPU'} value={`%${number(runtime.cpuPercent, 1)}`} tone={runtime.cpuPercent > 70 ? 'warning' : 'ok'} />
      <MetricCard label={tr ? 'Anlık RAM' : 'Current RAM'} value={runtime.memoryUsagePercent == null ? '—' : `%${number(runtime.memoryUsagePercent, 1)}`} detail={`${bytes(runtime.rssBytes)} RSS · ${runtime.memoryLimitSource === 'container' ? (tr ? 'container limiti' : 'container limit') : (tr ? 'sunucu kapasitesi' : 'host capacity')}`} tone={runtime.memoryUsagePercent > 75 ? 'warning' : 'ok'} />
      <MetricCard label={tr ? 'Anlık pool bekleyen' : 'Current pool waiting'} value={number(pool.waiting)} detail={`${number(pool.total)} / ${number(pool.max)}`} tone={pool.waiting > 0 ? 'danger' : 'ok'} />
    </div>

    {history && <Section title={tr ? 'Seçilen Dönemin Kalıcı Geçmişi' : 'Persistent History for Selected Range'} subtitle={tr ? 'Backend restartlarından etkilenmeyen Prometheus özeti' : 'Prometheus summary preserved across backend restarts'}>
      <div className="perfCompactGrid">
        <MetricCard label={tr ? 'CPU ortalama / tepe' : 'CPU average / peak'} value={`%${number(historicalRuntime.cpuAveragePercent, 1)} / %${number(historicalRuntime.cpuMaxPercent, 1)}`} tone={historicalRuntime.cpuMaxPercent > 70 ? 'warning' : 'ok'} />
        <MetricCard label={tr ? 'RSS ortalama / tepe' : 'RSS average / peak'} value={`${bytes(historicalRuntime.rssAverageBytes)} / ${bytes(historicalRuntime.rssMaxBytes)}`} />
        <MetricCard label={tr ? 'Event-loop p99 tepe' : 'Peak event-loop p99'} value={duration(historicalRuntime.eventLoopP99MaxMs)} tone={historicalRuntime.eventLoopP99MaxMs > 50 ? 'warning' : 'ok'} />
        <MetricCard label={tr ? 'Pool tepe / bekleyen tepe' : 'Peak pool / peak waiting'} value={`${number(historicalDatabase.poolMaxTotal)} / ${number(historicalDatabase.poolMaxWaiting)}`} tone={historicalDatabase.poolMaxWaiting > 0 ? 'danger' : 'ok'} />
        <MetricCard label={tr ? 'SSE ortalama / tepe' : 'SSE average / peak'} value={`${number(historicalSse.averageTotal, 1)} / ${number(historicalSse.maxTotal)}`} />
        <MetricCard label={tr ? 'En fazla çevrimiçi kullanıcı' : 'Peak online users'} value={number(historicalPresence.maxOnlineFamilyUsers)} />
        <MetricCard label={tr ? 'En yüksek konum kuyruğu' : 'Peak location queue'} value={number(historicalLocationQueue?.maxPending)} detail={duration(historicalLocationQueue?.maxOldestPendingAgeMs)} tone={historicalLocationQueue?.maxPending ? 'warning' : 'ok'} />
        <MetricCard label={tr ? 'Dönemde izlenen restart' : 'Restarts observed in range'} value={number(recentRestartCount)} tone={recentRestartCount ? 'warning' : 'ok'} />
      </div>
    </Section>}

    <Section title={tr ? 'SLO Kontrolü' : 'SLO Checks'} subtitle={tr ? 'Hedeflerin anlık değerlendirmesi' : 'Current target evaluation'}>
      <div className="perfSloGrid">
        {(slo.checks || []).map((check) => <div key={check.key} className={check.status}><span>{check.label}</span><strong>{number(check.value, 2)}{check.unit}</strong><small>≤ {number(check.target, 2)}{check.unit}</small></div>)}
      </div>
    </Section>

    <Section title={tr ? 'Endpoint Performansı' : 'Endpoint Performance'} subtitle={tr ? 'Kimlik ve sorgu parametrelerinden arındırılmış route istatistikleri' : 'Route statistics without identities or query parameters'}>
      {(http.endpoints || []).length ? <div className="perfTableWrap"><table><thead><tr><th>{tr ? 'Endpoint' : 'Endpoint'}</th><th>{tr ? 'İstek' : 'Requests'}</th><th>p50</th><th>p95</th><th>p99</th><th>5xx</th><th>{tr ? 'Ort. boyut' : 'Avg size'}</th><th>{endpointSizeHeading}</th></tr></thead><tbody>
        {http.endpoints.map((row) => <tr key={`${row.method}:${row.route}:${row.category}`}><td><b className={`perfMethod ${row.category}`}>{row.method}</b><span>{row.route}</span></td><td>{number(row.requests)}</td><td>{duration(row.p50Ms)}</td><td className={row.p95Ms > (row.category === 'write' ? 700 : 300) ? 'warnText' : ''}>{duration(row.p95Ms)}</td><td className={row.p99Ms > 1500 ? 'dangerText' : ''}>{duration(row.p99Ms)}</td><td>{number(row.errors5xx)}</td><td>{bytes(row.averageBytes)}</td><td>{bytes(data?.source === 'prometheus' ? row.p95Bytes : row.maxBytes)}</td></tr>)}
      </tbody></table></div> : <Empty>{tr ? 'Seçilen aralıkta ölçülmüş istek yok.' : 'No measured requests in the selected range.'}</Empty>}
    </Section>

    <div className="perfTwoColumns">
      <Section title={tr ? 'PostgreSQL ve Havuz' : 'PostgreSQL and Pool'}>
        <div className="perfCompactGrid">
          <MetricCard label={tr ? 'Toplam bağlantı' : 'Total connections'} value={number(pool.total)} />
          <MetricCard label={tr ? 'Boş bağlantı' : 'Idle connections'} value={number(pool.idle)} />
          <MetricCard label={tr ? 'Bekleyen' : 'Waiting'} value={number(pool.waiting)} tone={pool.waiting ? 'danger' : 'ok'} />
          <MetricCard label={tr ? 'Bağlantı bekleme p95' : 'Connection wait p95'} value={duration(connectionWaitP95)} />
        </div>
        {(database.queries || []).length ? <div className="perfMiniRows">{database.queries.slice(0, 10).map((query) => <div key={`${query.operation}:${query.relation}`}><span>{query.operation} · {query.relation}</span><strong>{duration(query.p95Ms)}</strong><small>{number(query.count)}×</small></div>)}</div> : <Empty>{tr ? 'Sorgu ölçümü bekleniyor.' : 'Waiting for query metrics.'}</Empty>}
        {database.pgStatements?.available && <div className="perfSubTitle">{tr ? 'Toplam maliyeti en yüksek sorgular' : 'Highest total query cost'}</div>}
        {database.pgStatements?.available && <div className="perfMiniRows">{(database.pgStatements.queries || []).slice(0, 8).map((query) => <div key={query.fingerprint}><span>{query.operation} · {query.relation} · {query.fingerprint}</span><strong>{duration(query.meanMs)}</strong><small>{number(query.calls)}×</small></div>)}</div>}
      </Section>

      <Section title={tr ? 'Canlı Bağlantılar ve Kuyruklar' : 'Live Connections and Queues'}>
        <div className="perfCompactGrid">
          <MetricCard label={tr ? 'Toplam SSE' : 'Total SSE'} value={number(data?.sse?.total)} detail={`${number(data?.sse?.map)} ${tr ? 'harita' : 'map'} · ${number(data?.sse?.familyData)} data`} />
          <MetricCard label={tr ? 'Konum kuyruğu' : 'Location queue'} value={number(locationQueue.pending)} detail={`${number(locationQueue.active)} ${tr ? 'aktif' : 'active'}`} tone={locationQueue.pending ? 'warning' : 'ok'} />
          <MetricCard label={tr ? 'En eski konum işi' : 'Oldest location job'} value={duration(locationQueue.oldestPendingAgeMs)} tone={locationQueue.oldestPendingAgeMs > 30000 ? 'warning' : 'ok'} />
          <MetricCard label={tr ? 'Bildirim kuyruğu' : 'Notification queue'} value={notificationQueue.available ? number(notificationQueue.pending) : '—'} detail={notificationQueue.available ? `${number(notificationQueue.failed)} ${tr ? 'başarısız' : 'failed'}` : (tr ? 'Kullanılamıyor' : 'Unavailable')} tone={notificationQueue.failed ? 'danger' : ''} />
        </div>
        <div className="perfMiniRows">
          <div><span>{tr ? 'Dönemde birleştirilen konum işleri' : 'Coalesced location jobs in range'}</span><strong>{number(locationPeriodTotals.coalesced)}</strong></div>
          <div><span>{tr ? 'Dönemde reddedilen konum işleri' : 'Rejected location jobs in range'}</span><strong className={locationPeriodTotals.rejected ? 'dangerText' : ''}>{number(locationPeriodTotals.rejected)}</strong></div>
          <div><span>{tr ? 'Dönemde tamamlanan konum işleri' : 'Completed location jobs in range'}</span><strong>{number(locationPeriodTotals.completed)}</strong></div>
        </div>
      </Section>
    </div>

    <div className="perfTwoColumns">
      <Section title={tr ? 'Auth ve Token Yenileme' : 'Auth and Token Refresh'}>
        <div className="perfCompactGrid">
          <MetricCard label="HTTP 401" value={number(auth.unauthorized401)} tone={auth.unauthorized401 ? 'warning' : 'ok'} />
          <MetricCard label={tr ? 'Deneme' : 'Attempts'} value={number(auth.refreshAttempts)} />
          <MetricCard label={tr ? 'Başarılı' : 'Successful'} value={number(auth.refreshSuccess)} tone="ok" />
          <MetricCard label={tr ? 'Geçersiz/eksik' : 'Invalid/missing'} value={number(auth.refreshInvalid)} tone={auth.refreshInvalid ? 'warning' : ''} />
          <MetricCard label={tr ? 'Sunucu hatası' : 'Server failures'} value={number(auth.refreshFailed)} tone={auth.refreshFailed ? 'danger' : 'ok'} />
        </div>
      </Section>
      <Section title={tr ? 'Process Sağlığı' : 'Process Health'}>
        <div className="perfMiniRows">
          <div><span>{tr ? 'Başlangıç' : 'Started'}</span><strong>{runtime.processStartedAt ? new Date(runtime.processStartedAt).toLocaleString(tr ? 'tr-TR' : 'en-US') : '—'}</strong></div>
          <div><span>Uptime</span><strong>{number((runtime.uptimeSeconds || 0) / 3600, 1)} {tr ? 'saat' : 'hours'}</strong></div>
          <div><span>{tr ? 'İzlenen restart' : 'Observed restarts'}</span><strong className={recentRestartCount ? 'dangerText' : ''}>{number(recentRestartCount)}</strong></div>
          <div><span>Heap</span><strong>{bytes(runtime.heapUsedBytes)} / {bytes(runtime.heapTotalBytes)}</strong></div>
          <div><span>RSS</span><strong>{bytes(runtime.rssBytes)}</strong></div>
        </div>
      </Section>
    </div>
  </div>;
}
